import { useToast } from '@n8n/composables/useToast';
import { useProjectsStore } from '@/features/collaboration/projects/projects.store';
import { useI18n } from '@n8n/i18n';
import { ref } from 'vue';
import { createResultError, createResultOk, type Result } from '@n8n/utils/result';
import {
	NodeHelpers,
	type CredentialInformation,
	type GenericValue,
	type ICredentialDataDecryptedObject,
	type ICredentialType,
	type INodeProperties,
} from 'n8n-workflow';

import { useCredentialsStore } from '../credentials.store';
import type { ICredentialsResponse } from '../credentials.types';
import { useTelemetry } from '@n8n/composables/useTelemetry';
import { useWorkflowsStore } from '@/app/stores/workflows.store';
import { useRootStore } from '@n8n/stores/useRootStore';
import { getTrustedOAuthOrigins, hasOAuthTokenData, waitForOAuthCallback } from './oauthCallback';

/**
 * Composable for OAuth credential type detection and authorization.
 * Used by NodeCredentials for the quick connect OAuth flow.
 */
export function useCredentialOAuth() {
	const credentialsStore = useCredentialsStore();
	const projectsStore = useProjectsStore();
	const workflowsStore = useWorkflowsStore();
	const rootStore = useRootStore();

	const toast = useToast();
	const i18n = useI18n();
	const telemetry = useTelemetry();

	const oauthAbortController = ref<AbortController | null>(null);
	const pendingCredentialId = ref<string | null>(null);

	/**
	 * Get parent types for a credential type (e.g., googleSheetsOAuth2Api extends googleOAuth2Api extends oAuth2Api).
	 */
	function getParentTypes(credentialTypeName: string, visited = new Set<string>()): string[] {
		if (visited.has(credentialTypeName)) return [];
		visited.add(credentialTypeName);

		const type = credentialsStore.getCredentialTypeByName(credentialTypeName);
		if (type?.extends === undefined) return [];

		const types: string[] = [];
		for (const typeName of type.extends) {
			types.push(typeName);
			types.push(...getParentTypes(typeName, visited));
		}
		return types;
	}

	/**
	 * Check if a credential type is an OAuth type (extends oAuth2Api or oAuth1Api).
	 */
	function isOAuthCredentialType(credentialTypeName: string): boolean {
		const parentTypes = getParentTypes(credentialTypeName);
		return (
			credentialTypeName === 'oAuth2Api' ||
			credentialTypeName === 'oAuth1Api' ||
			parentTypes.includes('oAuth2Api') ||
			parentTypes.includes('oAuth1Api')
		);
	}

	/**
	 * Check if a credential type is Google OAuth (extends googleOAuth2Api).
	 */
	function isGoogleOAuthType(credentialTypeName: string): boolean {
		const parentTypes = getParentTypes(credentialTypeName);
		return credentialTypeName === 'googleOAuth2Api' || parentTypes.includes('googleOAuth2Api');
	}

	/**
	 * Check if an OAuth credential type has all required fields managed/overwritten.
	 * This indicates the credential can be used with quick connect (just OAuth flow, no manual config).
	 * Reuses logic patterns from CredentialEdit.vue (credentialProperties + requiredPropertiesFilled).
	 */
	function canOAuthCredentialQuickConnect(credentialTypeName: string): boolean {
		if (!isOAuthCredentialType(credentialTypeName)) {
			return false;
		}

		const credentialType = credentialsStore.getCredentialTypeByName(credentialTypeName);
		if (!credentialType) {
			return false;
		}

		if (credentialType.__skipManagedCreation) {
			return false;
		}

		const overwrittenProperties = credentialType.__overwrittenProperties ?? [];
		const nonOverwrittenConfigurableProperties = getManuallyConfigurableProperties(
			credentialType,
		).filter((prop) => !overwrittenProperties.includes(prop.name));

		if (nonOverwrittenConfigurableProperties.length === 0) {
			return true;
		}

		if (overwrittenProperties.length === 0) {
			return false;
		}

		return nonOverwrittenConfigurableProperties.every(
			(prop) => prop.required !== true || (prop.type !== 'string' && prop.type !== 'number'),
		);
	}

	/**
	 * Returns properties the user must fill in. Walks the extends chain so
	 * inherited fields (e.g. `clientId`/`clientSecret` from `oAuth2Api`) are
	 * considered, and applies `displayOptions` against the effective defaults
	 * — matching the credential edit modal's `credentialProperties` /
	 * `displayCredentialParameter` logic.
	 */
	function getManuallyConfigurableProperties(credentialType: ICredentialType): INodeProperties[] {
		const mergedProperties = getMergedCredentialProperties(credentialType.name);
		const defaults: ICredentialDataDecryptedObject = {};
		for (const prop of mergedProperties) {
			defaults[prop.name] = prop.default as CredentialInformation;
		}

		return mergedProperties.filter((prop) => {
			if (prop.type === 'hidden' || prop.type === 'notice') return false;
			return NodeHelpers.displayParameter(defaults, prop, null, null);
		});
	}

	function getMergedCredentialProperties(
		credentialTypeName: string,
		visited = new Set<string>(),
	): INodeProperties[] {
		if (visited.has(credentialTypeName)) return [];
		visited.add(credentialTypeName);

		const credentialType = credentialsStore.getCredentialTypeByName(credentialTypeName);
		if (!credentialType) return [];
		if (credentialType.extends === undefined) return credentialType.properties;

		const merged: INodeProperties[] = [];
		for (const parentName of credentialType.extends) {
			NodeHelpers.mergeNodeProperties(merged, getMergedCredentialProperties(parentName, visited));
		}
		NodeHelpers.mergeNodeProperties(merged, credentialType.properties);
		return merged;
	}

	function hasManualCredentialInputFields(credentialType: ICredentialType): boolean {
		return getManuallyConfigurableProperties(credentialType).length > 0;
	}

	async function getOAuthAuthorizationUrl(
		credential: ICredentialsResponse,
	): Promise<Result<string, 'api-error' | 'no-url'>> {
		const parentTypes = getParentTypes(credential.type);

		try {
			if (credential.type === 'oAuth2Api' || parentTypes.includes('oAuth2Api')) {
				return createResultOk(await credentialsStore.oAuth2Authorize(credential));
			}
			if (credential.type === 'oAuth1Api' || parentTypes.includes('oAuth1Api')) {
				return createResultOk(await credentialsStore.oAuth1Authorize(credential));
			}
		} catch (error) {
			toast.showError(
				error,
				i18n.baseText('credentialEdit.credentialEdit.showError.generateAuthorizationUrl.title'),
			);
			return createResultError('api-error');
		}

		return createResultError('no-url');
	}

	function isValidHttpUrl(url: string): boolean {
		try {
			const parsed = new URL(url);
			return ['http:', 'https:'].includes(parsed.protocol);
		} catch {
			return false;
		}
	}

	function showOAuthUrlError(): void {
		toast.showError(
			new Error(i18n.baseText('credentialEdit.credentialEdit.showError.invalidOAuthUrl.message')),
			i18n.baseText('credentialEdit.credentialEdit.showError.invalidOAuthUrl.title'),
		);
	}

	function openOAuthPopup(url: string, signal?: AbortSignal): Window | null {
		const params =
			'scrollbars=no,resizable=yes,status=no,titlebar=no,location=no,toolbar=no,menubar=no,width=500,height=700';
		const popup = window.open(url, 'OAuth Authorization', params);

		signal?.addEventListener('abort', () => {
			popup?.close();
		});

		return popup;
	}

	async function isConnected(credentialId: string): Promise<boolean> {
		try {
			return hasOAuthTokenData(await credentialsStore.getCredentialData({ id: credentialId }));
		} catch {
			return false;
		}
	}

	/**
	 * Authorize OAuth credentials by opening a popup and listening for callback.
	 * Returns true if OAuth was successful, false if cancelled or failed.
	 */
	async function authorize(
		credential: ICredentialsResponse,
		signal?: AbortSignal,
	): Promise<boolean> {
		// Token presence in credential data can only confirm the flow for fixed
		// credentials that had no token before the popup opened: a reconnect's old
		// token would read as an immediate false success, and end-user
		// (resolvable) credentials store tokens per user outside the credential
		// data, so presence never changes there.
		const canVerifyConnected = !credential.isResolvable && !(await isConnected(credential.id));

		const urlResult = await getOAuthAuthorizationUrl(credential);
		if (!urlResult.ok) {
			if (urlResult.error === 'no-url') showOAuthUrlError();
			return false;
		}

		if (!isValidHttpUrl(urlResult.result)) {
			showOAuthUrlError();
			return false;
		}

		const popup = openOAuthPopup(urlResult.result, signal);
		if (!popup) {
			showOAuthUrlError();
			return false;
		}

		let outcome = await waitForOAuthCallback({
			popup,
			trustedOrigins: getTrustedOAuthOrigins(rootStore.urlBaseEditor),
			signal,
			verifyConnected: canVerifyConnected
				? async () => await isConnected(credential.id)
				: undefined,
		});

		// Timeout and abort can race the backend committing the token: authorization
		// can legitimately take longer than the timeout, and cancellation is not
		// always explicit user intent (NodeCredentials also cancels on unmount).
		// Re-check before treating the flow as failed — a wrong failure deletes the
		// credential in createAndAuthorize and would resurface "Credential not
		// found" on the callback page.
		if (
			(outcome === 'timeout' || outcome === 'aborted') &&
			canVerifyConnected &&
			(await isConnected(credential.id))
		) {
			outcome = 'success';
		}

		// No-op when the opener relationship was severed by the provider's COOP
		// policy; the callback page closes itself in that case.
		popup.close();

		if (outcome === 'success') {
			toast.showMessage({
				title: i18n.baseText('nodeCredentials.oauth.accountConnected'),
				type: 'success',
			});
		} else if (outcome !== 'aborted') {
			toast.showMessage({
				title: i18n.baseText('nodeCredentials.oauth.accountConnectionFailed'),
				type: 'error',
			});
		}

		return outcome === 'success';
	}

	/**
	 * Create a new OAuth credential and run the full authorization flow.
	 * Returns the credential on success, null on failure (cleans up automatically).
	 */
	async function createAndAuthorize(
		credentialTypeName: string,
		nodeType?: string,
	): Promise<ICredentialsResponse | null> {
		const credentialType = credentialsStore.getCredentialTypeByName(credentialTypeName);
		if (!credentialType) {
			return null;
		}

		const data: ICredentialDataDecryptedObject = {};
		const allowedHttpRequestDomainsProperty = credentialType.properties.find(
			(prop) => prop.name === 'allowedHttpRequestDomains',
		);
		if (!allowedHttpRequestDomainsProperty || allowedHttpRequestDomainsProperty.type !== 'hidden') {
			data.allowedHttpRequestDomains = 'none';
		}

		let credential: ICredentialsResponse;
		try {
			const name = await credentialsStore.getNewCredentialName({
				credentialTypeName,
				fallbackName: credentialType.displayName,
			});
			credential = await credentialsStore.createNewCredential(
				{
					id: '',
					name,
					type: credentialTypeName,
					data,
				},
				projectsStore.currentProject?.id,
				undefined,
				{ skipStoreUpdate: true },
			);

			telemetry.track('User created credentials', {
				credential_type: credential.type,
				credential_id: credential.id,
				workflow_id: workflowsStore.workflowId,
			});
		} catch (error) {
			toast.showError(error, i18n.baseText('nodeCredentials.showMessage.title'));
			return null;
		}

		const controller = new AbortController();
		oauthAbortController.value = controller;
		pendingCredentialId.value = credential.id;

		const success = await authorize(credential, controller.signal);

		oauthAbortController.value = null;
		pendingCredentialId.value = null;

		const trackProperties: Record<string, GenericValue> = {
			credential_type: credentialTypeName,
			workflow_id: workflowsStore.workflowId ?? null,
			credential_id: credential.id,
			is_complete: true,
			is_new: true,
			is_valid: success,
			uses_external_secrets: false,
		};

		if (nodeType) {
			trackProperties.node_type = nodeType;
		}

		telemetry.track('User saved credentials', trackProperties);

		if (success) {
			credentialsStore.upsertCredential(credential);

			return credential;
		}

		void credentialsStore.deleteCredential({ id: credential.id });
		return null;
	}

	/**
	 * Cancel any in-progress OAuth authorization and clean up the pending credential.
	 */
	function cancelAuthorize() {
		if (oauthAbortController.value) {
			oauthAbortController.value.abort();
		}
		const credentialId = pendingCredentialId.value;
		if (!credentialId) return;
		// Cancellation is not always explicit user intent — NodeCredentials also
		// cancels on unmount — so keep the credential if the OAuth callback
		// already landed and only delete when it really never connected.
		void isConnected(credentialId).then((connected) => {
			if (connected) {
				void credentialsStore.fetchAllCredentials();
			} else {
				void credentialsStore.deleteCredential({ id: credentialId });
			}
		});
	}

	return {
		getParentTypes,
		isOAuthCredentialType,
		isGoogleOAuthType,
		canOAuthCredentialQuickConnect,
		hasManualCredentialInputFields,
		authorize,
		createAndAuthorize,
		cancelAuthorize,
	};
}
